import { useState, useEffect, useMemo, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import TradingRecommendationCard from '@/components/trading/TradingRecommendationCard'
import PreviewRecommendationCard from '@/components/trading/PreviewRecommendationCard'
import UpgradePrompt from '@/components/common/UpgradePrompt'
import PageBasedPagination from '@/components/ui/PageBasedPagination'
import { faChartArea, faSpinner, faSort, faSortUp, faSortDown, faFilter, faSearch, faTimes, faHeart, faHeartRegular } from '@/fontawesome'
import { APIRoutes } from '@/services/api.routes'
import { PAGINATION } from '@/config'
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation'
import type { RecommendationCore, RecommendationEarnings, SortDirection, PreviewCoreRecommendation, PreviewEarningsRecommendation, RecommendationFavorite } from '@/models'
import type { GraphPoint } from '@/modelsAPI'
import { useUser } from '@/contexts/UserContext'

// Union type for Core and Earnings recommendations only
type AnyRecommendation = RecommendationCore | RecommendationEarnings;
import { rfcDateToTimestamp, formatRFCDate } from '@/utils/helpers'

interface TradingRecommendationsListProps {
  className?: string
  filterType: TradingRecommendationType
  showHistory?: boolean
  activeTitle?: string  // Optional custom title for active recommendations
  historyTitle?: string // Optional custom title for historical recommendations
  hasAccess?: boolean   // Whether user has access to this recommendation type
}

// Union type for preview recommendations
type AnyPreviewRecommendation = PreviewCoreRecommendation | PreviewEarningsRecommendation;

type RecommendationWithGraph = AnyRecommendation & {
  graphPoints?: GraphPoint[]
  graphLoading?: boolean
}

export default function TradingRecommendationsList({ className = '', filterType, showHistory = false, activeTitle, historyTitle, hasAccess = true }: TradingRecommendationsListProps) {
  const { isAdmin } = useUser()
  const [recommendations, setRecommendations] = useState<RecommendationWithGraph[]>([])
  const [allRecommendations, setAllRecommendations] = useState<RecommendationWithGraph[]>([])
  const [previewRecommendations, setPreviewRecommendations] = useState<AnyPreviewRecommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'startDate' | 'strikePrice'>('startDate')
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC')
  const [strikePriceFilter, setStrikePriceFilter] = useState<{ min?: number; max?: number }>({})
  const [tempStrikePriceFilter, setTempStrikePriceFilter] = useState<{ min?: number; max?: number }>({})
  const [showFilters, setShowFilters] = useState(false)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [symbolFilter, setSymbolFilter] = useState('')
  const [sectorFilter, setSectorFilter] = useState('')
  const [boostFilter, setBoostFilter] = useState<'all' | 'boost' | 'noboost'>('all')
  const [onlyFavorites, setOnlyFavorites] = useState(false)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const graphCache = useRef<Map<string, GraphPoint[] | undefined>>(new Map())
  const pageSize = PAGINATION.USER_PAGES.TRADING_RECOMMENDATIONS
  const isRangeInvalid = (tempStrikePriceFilter.min !== undefined && tempStrikePriceFilter.max !== undefined)
    ? tempStrikePriceFilter.min > tempStrikePriceFilter.max
    : false

  useEffect(() => {
    fetchActiveRecommendations()
    // Fetch preview recommendations if user doesn't have access
    if (!hasAccess) {
      fetchPreviewRecommendations()
    } else {
      setPreviewRecommendations([])
    }
  }, [filterType, showHistory, hasAccess])

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1)
  }, [filterType, strikePriceFilter, showHistory, symbolFilter, sectorFilter, boostFilter, onlyFavorites])

  // Apply sorting, filtering and pagination client-side
  useEffect(() => {
    applyClientSideProcessing()
  }, [allRecommendations, sortBy, sortDirection, strikePriceFilter, symbolFilter, sectorFilter, boostFilter, onlyFavorites, currentPage])

  const applyClientSideProcessing = () => {
    if (allRecommendations.length === 0) {
      setRecommendations([])
      return
    }

    let processedRecs = [...allRecommendations]

    // Apply symbol filter
    if (symbolFilter.trim() !== '') {
      processedRecs = processedRecs.filter(rec =>
        rec.symbolTicker.toLowerCase().includes(symbolFilter.toLowerCase())
      )
    }

    // Apply sector filter
    if (sectorFilter.trim() !== '') {
      processedRecs = processedRecs.filter(rec =>
        rec.symbol?.sector?.toLowerCase() === sectorFilter.toLowerCase()
      )
    }

    // Apply price filter (strikePrice only for Core/Earnings)
    if (strikePriceFilter.min !== undefined || strikePriceFilter.max !== undefined) {
      processedRecs = processedRecs.filter(rec => {
        const price = rec.strikePrice
        const minMatch = strikePriceFilter.min === undefined || price >= strikePriceFilter.min!
        const maxMatch = strikePriceFilter.max === undefined || price <= strikePriceFilter.max!
        return minMatch && maxMatch
      })
    }

    // Apply boost filter (admin only)
    if (boostFilter !== 'all') {
      processedRecs = processedRecs.filter(rec =>
        boostFilter === 'boost' ? rec.boost : !rec.boost
      )
    }

    // Apply favorites filter
    if (onlyFavorites) {
      processedRecs = processedRecs.filter(rec => rec.favorite !== null && rec.favorite !== undefined)
    }

    // Set total count after filtering but before pagination
    setTotalCount(processedRecs.length)

    // Apply sorting
    const directionFactor = sortDirection === 'ASC' ? 1 : -1
    processedRecs = processedRecs.sort((a, b) => {
      if (sortBy === 'startDate') {
        const taStr = rfcDateToTimestamp(a.startDate, false)
        const tbStr = rfcDateToTimestamp(b.startDate, false)
        const ta = Date.parse(taStr)
        const tb = Date.parse(tbStr)
        const taNum = isNaN(ta) ? 0 : ta
        const tbNum = isNaN(tb) ? 0 : tb
        return (taNum - tbNum) * directionFactor
      }
      // sortBy === 'strikePrice'
      return (a.strikePrice - b.strikePrice) * directionFactor
    })

    // Apply pagination
    const startIndex = (currentPage - 1) * pageSize
    processedRecs = processedRecs.slice(startIndex, startIndex + pageSize)

    // Re-attach already-loaded graphs from cache to avoid reloading charts on re-process
    const withGraphs = processedRecs.map(rec =>
      graphCache.current.has(rec.id)
        ? { ...rec, graphPoints: graphCache.current.get(rec.id), graphLoading: false }
        : rec
    )

    setRecommendations(withGraphs)

    // Fetch symbol graph data only for recommendations whose graph isn't cached yet
    withGraphs.forEach(rec => {
      if (rec.symbol?.id && !graphCache.current.has(rec.id)) {
        fetchSymbolGraph(rec)
      }
    })
  }

  const handleFavoriteChange = (id: string, favorite: RecommendationFavorite | null) => {
    setAllRecommendations(prev => prev.map(r => r.id === id ? { ...r, favorite } : r))
  }

  const fetchActiveRecommendations = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch recommendations from the appropriate API based on filter type
      let activeRecs: AnyRecommendation[];

      // Determine which states to query based on showHistory
      const states = showHistory ? ['FINISHED_ASSIGNED', 'FINISHED_UNASSIGNED'] : ['OPEN'];

      if (filterType === 'CORE') {
        // Fetch all recommendations without server-side sorting
        const response = await APIRoutes.listCoreRecommendations({
          limit: PAGINATION.FETCH_ALL_LIMIT, // Fetch all
          state: states
        });
        activeRecs = response.recommendations || [];
      } else if (filterType === 'EARNINGS') {
        // Fetch all recommendations without server-side sorting
        const response = await APIRoutes.listEarningsRecommendations({
          limit: PAGINATION.FETCH_ALL_LIMIT, // Fetch all
          state: states
        });
        activeRecs = response.recommendations || [];
      } else {
        // SWING type - no implementation yet
        activeRecs = [];
      }

      // Store all recommendations for client-side processing
      setAllRecommendations(activeRecs as RecommendationWithGraph[])
    } catch (err) {
      console.error('Failed to fetch trading recommendations:', err)
      setError('Fehler beim Laden der Tradingideen')
    } finally {
      setLoading(false)
    }
  }

  const fetchPreviewRecommendations = async () => {
    try {
      const states = showHistory ? ['FINISHED_ASSIGNED', 'FINISHED_UNASSIGNED'] : ['OPEN'];

      if (filterType === 'CORE') {
        const response = await APIRoutes.listPreviewCoreRecommendations({
          limit: PAGINATION.FETCH_ALL_LIMIT,
          state: states
        });
        // Filter out free ones (they're already in normal recommendations)
        const nonFreePreview = (response.recommendations ?? []).filter(r => !r.free);
        setPreviewRecommendations(nonFreePreview);
      } else if (filterType === 'EARNINGS') {
        const response = await APIRoutes.listPreviewEarningsRecommendations({
          limit: PAGINATION.FETCH_ALL_LIMIT,
          state: states
        });
        // Filter out free ones (they're already in normal recommendations)
        const nonFreePreview = (response.recommendations ?? []).filter(r => !r.free);
        setPreviewRecommendations(nonFreePreview);
      }
    } catch (err) {
      console.error('Failed to fetch preview recommendations:', err);
      // Don't set error - preview is optional
    }
  }

  const fetchSymbolGraph = async (rec: AnyRecommendation) => {
    if (!rec.symbol?.id) return

    try {
      // Update the recommendation to show graph loading
      setRecommendations(prev => prev.map(r =>
        r.id === rec.id ? { ...r, graphLoading: true } : r
      ))

      // Convert startDate/endDate (RFCDate) to RFC3339
      const start = rfcDateToTimestamp(rec.startDate, false)
      const end = rec.endDate
        ? rfcDateToTimestamp(rec.endDate, true)
        : new Date().toISOString()

      const response = await APIRoutes.getSymbolGraph(rec.symbol.id, {
        start,
        end,
        points: PAGINATION.CHART.CARD_POINTS
      })

      let points: GraphPoint[] | undefined = response.points
      if (points.length <= 1) points = undefined

      graphCache.current.set(rec.id, points)

      // Update the recommendation with the fetched graph data
      setRecommendations(prev => prev.map(r =>
        r.id === rec.id
          ? { ...r, graphPoints: points, graphLoading: false }
          : r
      ))
    } catch (err) {
      console.error('Failed to fetch symbol graph:', err)
      // Just mark as not loading on error
      setRecommendations(prev => prev.map(r =>
        r.id === rec.id ? { ...r, graphLoading: false } : r
      ))
    }
  }

  const handleSort = (newSortBy: 'startDate' | 'strikePrice') => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(newSortBy)
      setSortDirection(newSortBy === 'strikePrice' ? 'ASC' : 'DESC')
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  }

  const handleTempFilterChange = (min?: string, max?: string) => {
    setTempStrikePriceFilter({
      min: min && min !== '' ? parseFloat(min) : undefined,
      max: max && max !== '' ? parseFloat(max) : undefined
    })
  }

  const applyFilters = () => {
    const centsFilter = {
      min: tempStrikePriceFilter.min !== undefined ? Math.round(tempStrikePriceFilter.min * 100) : undefined,
      max: tempStrikePriceFilter.max !== undefined ? Math.round(tempStrikePriceFilter.max * 100) : undefined,
    }
    setStrikePriceFilter(centsFilter)
    updateActiveFilters(centsFilter, symbolFilter, sectorFilter, boostFilter)
  }

  const updateActiveFilters = (priceFilter: typeof strikePriceFilter, symbol: string, sector: string, boost: 'all' | 'boost' | 'noboost') => {
    const hasPriceFilter = priceFilter.min !== undefined || priceFilter.max !== undefined
    const hasSymbolFilter = symbol.trim() !== ''
    const hasSectorFilter = sector.trim() !== ''
    const hasBoostFilter = boost !== 'all'
    setHasActiveFilters(hasPriceFilter || hasSymbolFilter || hasSectorFilter || hasBoostFilter)
  }

  const handleSymbolFilterChange = (value: string) => {
    setSymbolFilter(value)
    updateActiveFilters(strikePriceFilter, value, sectorFilter, boostFilter)
  }

  const handleSectorFilterChange = (value: string) => {
    setSectorFilter(value)
    updateActiveFilters(strikePriceFilter, symbolFilter, value, boostFilter)
  }

  const resetFilters = () => {
    setTempStrikePriceFilter({})
    setStrikePriceFilter({})
    setSymbolFilter('')
    setSectorFilter('')
    setBoostFilter('all')
    setOnlyFavorites(false)
    setHasActiveFilters(false)
  }

  // Get unique sectors from all recommendations
  const availableSectors = Array.from(new Set(allRecommendations
    .map(rec => rec.symbol?.sector)
    .filter((sector): sector is string => sector !== null && sector !== undefined && sector !== '')
  )).sort()

  // Group recommendations by endDate (only when sorting by startDate)
  const groupedRecommendations = useMemo(() => {
    // If not sorting by startDate, don't group - return flat structure
    if (sortBy !== 'startDate') {
      return [['', recommendations]] as [string, typeof recommendations][]
    }

    const groups = recommendations.reduce((acc, rec) => {
      const endDate = rec.endDate
      if (!acc[endDate]) {
        acc[endDate] = []
      }
      acc[endDate].push(rec)
      return acc
    }, {} as Record<string, typeof recommendations>)

    // Convert to sorted array of [date, recs[]] tuples
    // Sort groups by endDate, respecting sort direction
    return Object.entries(groups).sort((a, b) => {
      const comparison = a[0].localeCompare(b[0])
      // If sorting DESC, show latest endDates first
      return sortDirection === 'DESC' ? -comparison : comparison
    })
  }, [recommendations, sortBy, sortDirection])

  const getSortIcon = (field: 'startDate' | 'strikePrice') => {
    if (sortBy !== field) return faSort
    return sortDirection === 'ASC' ? faSortUp : faSortDown
  }

  if (loading) {
    return (
      <div className={`text-center ${className}`}>
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`max-w-4xl mx-auto ${className}`}>
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      </div>
    )
  }

  if (recommendations.length === 0) {
    return (
      <div className={`max-w-7xl mx-auto ${className}`}>
        {/* Symbol Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Symbol filtern (z.B. AAPL, TSLA)..."
              value={symbolFilter}
              onChange={(e) => handleSymbolFilterChange(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            {symbolFilter && (
              <button
                onClick={() => handleSymbolFilterChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Controls auch bei leeren Ergebnissen anzeigen */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {showHistory
              ? (historyTitle || 'Historische Tradingideen')
              : (activeTitle || 'Aktive Tradingideen')
            }
          </h3>

          <div className="flex flex-col sm:flex-row gap-2">
            {/* Sort Controls */}
            <div className="flex gap-2">
              <button
                onClick={() => handleSort('startDate')}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                  sortBy === 'startDate'
                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Datum
                <FontAwesomeIcon icon={getSortIcon('startDate')} className="w-3 h-3" />
              </button>

              <button
                onClick={() => handleSort('strikePrice')}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                  sortBy === 'strikePrice'
                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Ausübungspreis
                <FontAwesomeIcon icon={getSortIcon('strikePrice')} className="w-3 h-3" />
              </button>
            </div>

            {/* Favorites Toggle */}
            <button
              onClick={() => { setOnlyFavorites(!onlyFavorites); setCurrentPage(1) }}
              title="Nur Favoriten anzeigen"
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                onlyFavorites
                  ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={onlyFavorites ? faHeart : faHeartRegular} className="w-3 h-3" />
              Favoriten
            </button>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
              Filter
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                  {[
                    strikePriceFilter.min !== undefined || strikePriceFilter.max !== undefined,
                    symbolFilter.trim() !== '',
                    sectorFilter.trim() !== '',
                    isAdmin && boostFilter !== 'all'
                  ].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Filter</h4>
            <div className="flex flex-col gap-4">
              {/* Sector Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 w-20">Branche:</label>
                <select
                  value={sectorFilter}
                  onChange={(e) => handleSectorFilterChange(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Alle Branchen</option>
                  {availableSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>

              {/* Boost Filter - nur für Admins */}
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300 w-20 shrink-0">Boost:</label>
                  <select
                    value={boostFilter}
                    onChange={(e) => {
                      const val = e.target.value as 'all' | 'boost' | 'noboost'
                      setBoostFilter(val)
                      updateActiveFilters(strikePriceFilter, symbolFilter, sectorFilter, val)
                    }}
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="all">Alle</option>
                    <option value="boost">Boost</option>
                    <option value="noboost">Kein Boost</option>
                  </select>
                </div>
              )}

              {/* Price Filter */}
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Ausübungspreis
                </h5>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Von:</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Min"
                      value={tempStrikePriceFilter.min ?? ''}
                      className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      onChange={(e) => handleTempFilterChange(e.target.value, tempStrikePriceFilter.max?.toString())}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Bis:</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Max"
                      value={tempStrikePriceFilter.max ?? ''}
                      className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      onChange={(e) => handleTempFilterChange(tempStrikePriceFilter.min?.toString(), e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {isRangeInvalid && (
                <div className="text-sm text-red-600 dark:text-red-400">Überprüfen sie ihre eingabe.</div>
              )}
              <div className="flex gap-2 justify-start">
                <button
                  onClick={applyFilters}
                  disabled={isRangeInvalid}
                  className={`px-4 py-2 text-sm rounded-lg cursor-pointer transition-colors ${
                    isRangeInvalid
                      ? 'bg-blue-300 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Übernehmen
                </button>
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">
            <FontAwesomeIcon icon={faChartArea} className="w-16 h-16 mb-4 mx-auto" />
            <h3 className="text-xl font-semibold mb-2">
              {(hasActiveFilters || onlyFavorites)
                ? 'Keine Trades gefunden'
                : showHistory
                  ? 'Keine historischen Trades vorhanden'
                  : !hasAccess
                    ? 'Aktuell keine kostenlosen Trades vorhanden'
                    : 'Aktuell keine Trades vorhanden'}
            </h3>
            <p className="text-sm">
              {(hasActiveFilters || onlyFavorites)
                ? 'Mit den aktuellen Filterkriterien wurden keine Tradingideen gefunden. Passen Sie die Filter an, um andere Ergebnisse zu sehen.'
                : showHistory
                  ? 'In dieser Kategorie sind derzeit keine historischen Tradingideen verfügbar.'
                  : 'In dieser Kategorie sind derzeit keine aktiven Tradingideen verfügbar.'
              }
            </p>
          </div>
        </div>

        {/* Preview Section - auch im Empty State anzeigen */}
        {!hasAccess && !showHistory && previewRecommendations.length > 0 && (
          <div className="mt-8">
            <UpgradePrompt
              previewCount={previewRecommendations.length}
              featureName={filterType === 'CORE' ? 'Kernstrategie-Tradingideen' : 'Earnings-Tradingideen'}
              className="mb-6"
            />
            <div className="space-y-4">
              {previewRecommendations.map((preview) => (
                <PreviewRecommendationCard
                  key={preview.id}
                  preview={preview}
                  type={filterType}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`max-w-7xl mx-auto ${className}`}>
      {/* Symbol Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Symbol filtern (z.B. AAPL, TSLA)..."
            value={symbolFilter}
            onChange={(e) => handleSymbolFilterChange(e.target.value)}
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
          {symbolFilter && (
            <button
              onClick={() => handleSymbolFilterChange('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
            >
              <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {showHistory
            ? (historyTitle || 'Historische Tradingideen')
            : (activeTitle || 'Aktive Tradingideen')
          }
        </h3>

        <div className="flex flex-col sm:flex-row gap-2">
          {/* Sort Controls */}
          <div className="flex gap-2">
            <button
              onClick={() => handleSort('startDate')}
              className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
                sortBy === 'startDate'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Datum
              <FontAwesomeIcon icon={getSortIcon('startDate')} className="w-3 h-3" />
            </button>

            <button
              onClick={() => handleSort('strikePrice')}
              className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
                sortBy === 'strikePrice'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <span className="hidden sm:inline">Ausübungspreis</span>
              <span className="sm:hidden">Strike</span>
              <FontAwesomeIcon icon={getSortIcon('strikePrice')} className="w-3 h-3" />
            </button>
          </div>

          {/* Favorites Toggle */}
          <button
            onClick={() => { setOnlyFavorites(!onlyFavorites); setCurrentPage(1) }}
            title="Nur Favoriten anzeigen"
            className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
              onlyFavorites
                ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={onlyFavorites ? faHeart : faHeartRegular} className="w-3 h-3" />
            Favoriten
          </button>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
            Filter
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                {[
                  strikePriceFilter.min !== undefined || strikePriceFilter.max !== undefined,
                  symbolFilter.trim() !== '',
                  sectorFilter.trim() !== '',
                  isAdmin && boostFilter !== 'all'
                ].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-6 p-3 lg:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Filter</h4>
          <div className="flex flex-col gap-4">
            {/* Sector Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 w-20">Branche:</label>
              <select
                value={sectorFilter}
                onChange={(e) => handleSectorFilterChange(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Alle Branchen</option>
                {availableSectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            {/* Boost Filter - nur für Admins */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 w-20 shrink-0">Boost:</label>
                <select
                  value={boostFilter}
                  onChange={(e) => {
                    const val = e.target.value as 'all' | 'boost' | 'noboost'
                    setBoostFilter(val)
                    updateActiveFilters(strikePriceFilter, symbolFilter, sectorFilter, val)
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">Alle</option>
                  <option value="boost">Boost</option>
                  <option value="noboost">Kein Boost</option>
                </select>
              </div>
            )}

            {/* Price Filter */}
            <div>
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Ausübungspreis
              </h5>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Von:</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Min"
                    value={tempStrikePriceFilter.min ?? ''}
                    className="w-full sm:w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    onChange={(e) => handleTempFilterChange(e.target.value, tempStrikePriceFilter.max?.toString())}
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Bis:</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Max"
                    value={tempStrikePriceFilter.max ?? ''}
                    className="w-full sm:w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    onChange={(e) => handleTempFilterChange(tempStrikePriceFilter.min?.toString(), e.target.value)}
                  />
                </div>
              </div>
            </div>
            {isRangeInvalid && (
              <div className="text-sm text-red-600 dark:text-red-400">Überprüfen sie ihre eingabe.</div>
            )}
            <div className="flex gap-2 justify-start">
              <button
                onClick={applyFilters}
                disabled={isRangeInvalid}
                className={`px-4 py-2 text-sm rounded-lg cursor-pointer transition-colors ${
                  isRangeInvalid
                    ? 'bg-blue-300 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Übernehmen
              </button>
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
              >
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {groupedRecommendations.map(([endDate, recs]) => (
          <div key={endDate || 'all'}>
            {/* Date Separator - only show when sorting by startDate */}
            {sortBy === 'startDate' && endDate && (
              <div className="flex items-center mb-3 lg:mb-6">
                <div className="grow border-t border-gray-300 dark:border-gray-600"></div>
                <span className="px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                  End: {formatRFCDate(endDate)}
                </span>
                <div className="grow border-t border-gray-300 dark:border-gray-600"></div>
              </div>
            )}

            {/* Trades in this group */}
            <div className="space-y-4">
              {recs.map((rec) => (
                <TradingRecommendationCard
                  key={rec.id}
                  recommendation={rec}
                  recommendationType={filterType}
                  onFavoriteChange={(fav) => handleFavoriteChange(rec.id, fav)}
                  expanded={expandedCards.has(rec.id)}
                  onToggle={() => {
                    setExpandedCards(prev => {
                      const newSet = new Set(prev)
                      if (newSet.has(rec.id)) {
                        newSet.delete(rec.id)
                      } else {
                        newSet.add(rec.id)
                      }
                      return newSet
                    })
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Preview Section - shown when user lacks access and there are preview items */}
      {!hasAccess && !showHistory && previewRecommendations.length > 0 && (
        <div className="mt-8">
          {/* Upgrade CTA before preview cards */}
          <UpgradePrompt
            previewCount={previewRecommendations.length}
            featureName={filterType === 'CORE' ? 'Kernstrategie-Tradingideen' : 'Earnings-Tradingideen'}
            className="mb-6"
          />

          {/* Blurred preview cards */}
          <div className="space-y-4">
            {previewRecommendations.map((preview) => (
              <PreviewRecommendationCard
                key={preview.id}
                preview={preview}
                type={filterType}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="mt-6">
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}
    </div>
  )
}
